import { useEffect, useState } from 'react'
import { effacerReceptionniste, lireReceptionniste, montantCad, nomReceptionniste, type Receptionniste } from '@/lib/receptionniste'
import { Link, useNavigate, useParams } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { fetchCompanies, type CompanyInfo } from '@/api/auth'
import { useAuthStore } from '@/stores/useAuthStore'
import { defaultLocale } from '@/lib/locales'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { useLogout } from '@/hooks/useLogout'
import type { SubscriptionPlan } from '@/api/subscription'
import { usePricingParams, type Currency } from '@/hooks/usePricingParams'
import type { BillingPeriod } from '@/components/manager/subscription/plans'
import { Segmented } from '@/components/pricing/Segmented'
import { PricingPlanGrid } from '@/components/pricing/PricingPlanGrid'
import { PricingComparisonTable } from '@/components/pricing/PricingComparisonTable'

export function SubscribePage() {
  const { locale = defaultLocale } = useParams<{ locale: string }>()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const logout = useLogout()
  useDocumentTitle(t('subscribe.pageTitle', 'Choisir un plan'))

  const { billing, currency, setBilling, setCurrency } = usePricingParams()

  // Charge les infos de la company pour les guards d'accès
  const companiesQuery = useQuery({
    queryKey: ['companies-subscribe'],
    queryFn: fetchCompanies,
    retry: false,
  })

  const company: CompanyInfo | undefined = companiesQuery.data?.companies?.[0]

  // Réceptionniste IA choisie sur le site avant l'inscription (cookie).
  // Au-dessus du retour anticipé plus bas, comme tous les hooks : en dessous,
  // la page perdait un hook quand la garde se fermait après le chargement, et
  // React plantait (erreur n° 300, écran « Oups » vu le 26 oct.).
  const [receptionniste, setReceptionniste] = useState<Receptionniste | null>(() => lireReceptionniste())

  // Guards d'accès — navigation déférée en useEffect pour éviter l'erreur
  // "setState during render" de RouterProvider.
  const isAgent = user?.role === 'AGENT'
  // Un numéro acheté vaut une étape 1 finie : depuis le 26 oct., l'achat vide
  // preferred_phone_number, et l'entreprise passait pour une inscription à refaire.
  const aUnNumero = !!(company?.preferred_phone_number || company?.phone_number || company?.has_active_number)
  const onboardingIncomplete =
    !!companiesQuery.data &&
    (!aUnNumero ||
      company?.name?.startsWith('Nouvelle entreprise'))

  useEffect(() => {
    if (isAgent) {
      navigate(`/${locale}/dashboard`, { replace: true })
      return
    }
    if (onboardingIncomplete) {
      navigate(`/${locale}/onboarding/step1`, { replace: true })
    }
  }, [isAgent, onboardingIncomplete, navigate, locale])

  // Bloque le rendu pendant la redirection pour éviter le flash de contenu.
  if (isAgent || onboardingIncomplete) {
    return null
  }

  const retirerReceptionniste = () => {
    effacerReceptionniste()
    setReceptionniste(null)
  }

  const handleCheckout = (
    planSlug: SubscriptionPlan,
    billingPeriod: BillingPeriod,
    isTrial: boolean,
  ) => {
    // Redirige vers la page Checkout Elements interne (pas Stripe hosted).
    const params = new URLSearchParams({
      plan: planSlug,
      billing: billingPeriod,
    })
    if (isTrial) params.set('trial', '1')
    if (receptionniste) params.set('receptionist', receptionniste.slug)
    navigate(`/${locale}/checkout?${params.toString()}`)
  }

  const handleLogout = () => {
    logout()
  }

  // Loading state pendant la vérification onboarding
  if (companiesQuery.isLoading) {
    return (
      <main className="h-full flex items-center justify-center bg-[#0A192F]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white" />
      </main>
    )
  }

  return (
    <main className="h-full overflow-y-auto bg-[#0A192F] text-white">
      {/* Top bar : logo (gauche) + titre+subtitle (centre) + logout (droite) */}
      <header className="border-b border-white/5">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-8 py-3 grid grid-cols-[1fr_auto_1fr] items-center gap-4">
          <Link to={`/${locale}/dashboard`} className="flex items-center gap-2 group justify-self-start">
            <img
              src="https://sdk.callizy.ca/assets/favicon_callizy.png"
              alt="Callizy"
              width={24}
              height={24}
              className="h-6 w-6 object-contain"
            />
            <span className="font-semibold tracking-tight text-[15px] group-hover:text-white/80 transition-colors">
              Callizy
            </span>
          </Link>

          <div className="text-center">
            <h1 className="text-base sm:text-lg font-semibold tracking-tight leading-tight">
              {t('subscribe.heroTitle', 'Choisissez votre plan')}
            </h1>
            <p className="hidden sm:block text-[12px] text-white/55 leading-tight mt-0.5">
              {t(
                'subscribe.heroSubtitle',
                '14 jours gratuits, puis facturé selon le plan choisi. Annulable à tout moment.',
              )}
            </p>
          </div>

          <button
            type="button"
            onClick={handleLogout}
            className="justify-self-end text-[13px] text-white/55 hover:text-white transition-colors"
          >
            {t('subscribe.logout', 'Se déconnecter')}
          </button>
        </div>
      </header>

      {/* Sélecteurs vue / billing / currency */}
      <div className="max-w-[1440px] mx-auto px-6 lg:px-8 pt-2 pb-4">
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-5">
          <Segmented<BillingPeriod>
            tone="dark"
            value={billing}
            onChange={setBilling}
            ariaLabel="Période de facturation"
            options={[
              { id: 'monthly', label: t('subscribe.monthly', 'Mensuel') },
              { id: 'yearly', label: t('subscribe.yearly', 'Annuel'), suffix: '−20%' },
            ]}
          />
          <span className="hidden sm:block w-px h-6 bg-white/15" />
          <Segmented<Currency>
            tone="dark"
            value={currency}
            onChange={setCurrency}
            ariaLabel="Devise"
            options={[
              { id: 'cad', label: 'CAD' },
              { id: 'usd', label: 'USD' },
            ]}
          />
        </div>

        {currency === 'usd' && (
          <p className="mt-4 text-center text-[12.5px] text-white/50 max-w-md mx-auto">
            {t(
              'subscribe.currencyNote',
              'Conversion à titre informatif (1 CAD ≈ 0.73 USD). Facturation toujours en CAD.',
            )}
          </p>
        )}
      </div>

      {receptionniste && (
        <div className="max-w-[1440px] mx-auto px-6 lg:px-8">
          <div className="max-w-3xl mx-auto rounded-xl bg-blue-500/10 ring-1 ring-blue-400/40 px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-[13.5px]">
            <p className="flex-1 text-white/85">
              <span className="font-semibold text-white">{nomReceptionniste(receptionniste, locale)}</span>
              {' · '}
              {/* En annuel, le prix par mois comme les forfaits : 12 mois moins 20 %, facturé à l'année. */}
              {billing === 'yearly'
                ? t('subscribe.receptionist.lineYearly', '{{minutes}} min pour {{price}} $ par mois, facturé annuellement', { minutes: receptionniste.minutes, price: montantCad(receptionniste.priceYearlyCad / 12, locale) })
                : t('subscribe.receptionist.line', '{{minutes}} min pour {{price}} $ par mois', { minutes: receptionniste.minutes, price: receptionniste.priceCad })}
              {' · '}
              {t('subscribe.receptionist.when', "ajoutée à votre abonnement dès qu'il est payant, jamais pendant l'essai.")}
            </p>
            <button type="button" onClick={retirerReceptionniste} className="text-white/60 hover:text-white underline underline-offset-2 self-start sm:self-auto">
              {t('subscribe.receptionist.remove', 'Retirer')}
            </button>
          </div>
        </div>
      )}

      {/* Grille des plans */}
      <PricingPlanGrid
        billing={billing}
        currency={currency}
        locale={locale}
        onCheckout={handleCheckout}
      />

      {/* Comparatif détaillé */}
      <PricingComparisonTable />
    </main>
  )
}

export default SubscribePage
